'use client'

import { usePathname } from 'next/navigation'
import type { ReactNode } from 'react'
import { BusinessMembershipPaymentModal } from '@/components/alerts/BusinessMembershipPaymentModal'
import Link from 'next/link'
import { ArrowRight, LockKeyhole } from 'lucide-react'

type StartWhitelabelGateProps = {
  isOwner: boolean
  planName: string
  children?: ReactNode
  enabled?: boolean
  membershipId?: number
}

/** Bloqueo server-side de dominios whitelabel para comunidades Start. */
export function StartWhitelabelGate({
  isOwner,
  planName,
  children,
  enabled = true,
  membershipId = 5,
}: StartWhitelabelGateProps) {
  const pathname = usePathname() || '/'
  const isMembershipRoute = pathname === '/membership' || pathname.startsWith('/membership/')
  if (!enabled || (isOwner && isMembershipRoute)) return <>{children}</>

  function openCheckout() {
    window.dispatchEvent(new CustomEvent('hubee:business_membership_payment_open', {
      detail: { mode: 'renew', membershipId },
    }))
  }

  return (
    <>
    <main className="flex min-h-screen items-center justify-center bg-background px-5 py-10 text-foreground">
      <section className="w-full max-w-[480px] rounded-[28px] border border-border/60 bg-background p-7 text-center shadow-[0_30px_100px_rgba(0,0,0,0.12)] sm:p-9">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <LockKeyhole aria-hidden="true" className="h-7 w-7" />
        </div>

        {isOwner ? (
          <>
            <h1 className="mt-6 text-2xl font-semibold tracking-tight">
              Activa tu dominio personalizado
            </h1>
            <p className="mt-4 text-sm leading-7 text-muted-foreground">
              Tu membresía actual permite administrar tu comunidad desde Hubee.
              Para utilizar tu propio dominio necesitas actualizar a{' '}
              <strong className="font-semibold text-foreground">{planName}</strong>.
            </p>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">
              Tu contenido, miembros y configuración permanecen intactos.
            </p>
            <div className="mt-7 flex flex-col gap-3">
              <button
                type="button"
                onClick={openCheckout}
                className="flex min-h-12 items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground transition hover:opacity-90"
              >
                Actualizar membresía
                <ArrowRight aria-hidden="true" className="h-4 w-4" />
              </button>
              <Link
                href="https://hubee.app"
                className="flex min-h-12 items-center justify-center rounded-xl border border-border px-4 text-sm font-semibold transition hover:bg-muted"
              >
                Ir a Hubee
              </Link>
            </div>
          </>
        ) : (
          <>
            <h1 className="mt-6 text-2xl font-semibold tracking-tight">
              Esta comunidad está disponible en Hubee
            </h1>
            <p className="mt-4 text-sm leading-7 text-muted-foreground">
              Para acceder a esta comunidad, visita Hubee e inicia sesión con tu cuenta.
            </p>
            <Link
              href="https://hubee.app"
              className="mt-7 flex min-h-12 items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground transition hover:opacity-90"
            >
              Ir a Hubee
              <ArrowRight aria-hidden="true" className="h-4 w-4" />
            </Link>
          </>
        )}
      </section>
    </main>
    {isOwner ? <BusinessMembershipPaymentModal /> : null}
    </>
  )
}
